import {
  type FallbackReason,
  type FallbackRuntime,
  RunEvent,
  type RunStatus,
  TERMINAL_RUN_STATUSES,
} from '@af/shared';

/**
 * Pure reducer: RunEvents (as streamed over SSE, with seq) → renderable message parts.
 * Deterministic and idempotent per seq, so it is safe to replay after reconnect/resume.
 */

export type ApprovalState = {
  approvalId: string;
  inputPreview: string;
  status: 'pending' | 'approved' | 'denied';
  decidedBy?: string;
};

export type ToolPart = {
  kind: 'tool';
  seq: number;
  stepIdx: number;
  toolCallId: string;
  toolName: string;
  input: unknown;
  effect: string;
  /** `interrupted`: the run ended before this call returned (stopped, cancelled or a limit). */
  state: 'running' | 'awaiting_approval' | 'done' | 'error' | 'denied' | 'interrupted';
  output?: unknown;
  approval?: ApprovalState;
};

type FallbackEffort = NonNullable<Extract<RunEvent, { type: 'model-fallback' }>['toReasoningEffort']>;

/** The run continued on the other subscription runtime (Claude ⇄ Codex), at this point of the run. */
export type FallbackPart = {
  kind: 'fallback';
  seq: number;
  from: FallbackRuntime;
  to: FallbackRuntime;
  fromModelRef?: string;
  toModelRef: string;
  toReasoningEffort?: FallbackEffort;
  reason: FallbackReason;
};

export type RunPart =
  | { kind: 'text'; seq: number; stepIdx: number; text: string }
  | { kind: 'reasoning'; seq: number; stepIdx: number; text: string }
  | ToolPart
  | { kind: 'subagent'; seq: number; childRunId: string; role: string }
  | { kind: 'log'; seq: number; level: 'debug' | 'info' | 'warn' | 'error'; message: string }
  | { kind: 'egress'; seq: number; agentId: string; hosts: string[] }
  | FallbackPart
  | {
      kind: 'flow';
      seq: number;
      stepId: string;
      stepKind: string;
      title: string;
      status: 'started' | 'ok' | 'skipped' | 'failed' | 'waiting';
      costUsd?: number;
      error?: string;
    };

export type StepUsage = {
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  reasoningTokens: number;
  costUsd: number;
};

export interface RunViewState {
  lastSeq: number;
  status: RunStatus | null;
  error?: string;
  parts: RunPart[];
  steps: Record<number, StepUsage & { seq: number }>;
  totals: StepUsage;
  statusHistory: { seq: number; status: RunStatus; error?: string }[];
  finishReason?: string;
  /** Usage-limit wait metadata, kept in the reload snapshot as well as the live stream. */
  scheduled?: { reason: string; provider: string | null; resumeAt: string };
  /** `provider:model` the run continues on after its last fallback; absent while it never switched. */
  activeModelRef?: string;
  /** Effort the active fallback model runs with; absent when none applies or while on the agent's own model. */
  activeReasoningEffort?: FallbackEffort;
}

const ZERO: StepUsage = {
  inputTokens: 0,
  outputTokens: 0,
  cacheReadTokens: 0,
  cacheWriteTokens: 0,
  reasoningTokens: 0,
  costUsd: 0,
};

export function initialRunState(): RunViewState {
  return { lastSeq: 0, status: null, parts: [], steps: {}, totals: { ...ZERO }, statusHistory: [] };
}

function updateTool(
  parts: RunPart[],
  pred: (p: ToolPart) => boolean,
  fn: (p: ToolPart) => ToolPart,
): RunPart[] | null {
  const i = parts.findIndex((p) => p.kind === 'tool' && pred(p));
  if (i === -1) return null;
  const next = parts.slice();
  next[i] = fn(parts[i] as ToolPart);
  return next;
}

export function reduceRunEvent(state: RunViewState, seq: number, ev: RunEvent): RunViewState {
  if (seq <= state.lastSeq) return state; // duplicate after reconnect
  const s: RunViewState = { ...state, lastSeq: seq };

  switch (ev.type) {
    case 'status': {
      s.status = ev.status;
      s.scheduled =
        ev.status === 'scheduled' && ev.resumeAt
          ? { reason: ev.reason ?? 'usage_limit', provider: ev.provider ?? null, resumeAt: ev.resumeAt }
          : undefined;
      s.parts = settleToolParts(state.parts, ev.status);
      if (ev.error) s.error = ev.error;
      else if (!isTerminal(ev.status)) s.error = undefined; // a continued run is no longer failed
      s.statusHistory = [...state.statusHistory, { seq, status: ev.status, error: ev.error }];
      return s;
    }
    case 'text-delta':
    case 'reasoning-delta': {
      const kind = ev.type === 'text-delta' ? 'text' : 'reasoning';
      const last = state.parts[state.parts.length - 1];
      if (last && last.kind === kind && last.stepIdx === ev.stepIdx) {
        s.parts = [...state.parts.slice(0, -1), { ...last, text: last.text + ev.delta }];
      } else {
        s.parts = [...state.parts, { kind, seq, stepIdx: ev.stepIdx, text: ev.delta }];
      }
      return s;
    }
    case 'tool-call': {
      const updated = updateTool(
        state.parts,
        (p) => p.toolCallId === ev.toolCallId,
        (p) => ({ ...p, input: ev.input, effect: ev.effect, toolName: ev.toolName, stepIdx: ev.stepIdx }),
      );
      s.parts = updated ?? [
        ...state.parts,
        {
          kind: 'tool',
          seq,
          stepIdx: ev.stepIdx,
          toolCallId: ev.toolCallId,
          toolName: ev.toolName,
          input: ev.input,
          effect: ev.effect,
          state: 'running',
        },
      ];
      return s;
    }
    case 'tool-result': {
      const updated = updateTool(
        state.parts,
        (p) => p.toolCallId === ev.toolCallId,
        (p) => ({
          ...p,
          output: ev.output,
          state: ev.isError ? 'error' : p.state === 'denied' ? 'denied' : 'done',
        }),
      );
      s.parts = updated ?? [
        ...state.parts,
        {
          kind: 'tool',
          seq,
          stepIdx: -1,
          toolCallId: ev.toolCallId,
          toolName: ev.toolName,
          input: undefined,
          effect: 'unknown',
          output: ev.output,
          state: ev.isError ? 'error' : 'done',
        },
      ];
      return s;
    }
    case 'approval-requested': {
      const approval: ApprovalState = {
        approvalId: ev.approvalId,
        inputPreview: ev.inputPreview,
        status: 'pending',
      };
      const updated = updateTool(
        state.parts,
        (p) => p.toolCallId === ev.toolCallId,
        (p) => ({ ...p, state: 'awaiting_approval', approval }),
      );
      s.parts = updated ?? [
        ...state.parts,
        {
          kind: 'tool',
          seq,
          stepIdx: -1,
          toolCallId: ev.toolCallId,
          toolName: ev.toolName,
          input: undefined,
          effect: 'unknown',
          state: 'awaiting_approval',
          approval,
        },
      ];
      return s;
    }
    case 'approval-decided': {
      const updated = updateTool(
        state.parts,
        (p) => p.approval?.approvalId === ev.approvalId,
        (p) => ({
          ...p,
          state: ev.approved ? (p.state === 'awaiting_approval' ? 'running' : p.state) : 'denied',
          approval: { ...p.approval!, status: ev.approved ? 'approved' : 'denied', decidedBy: ev.decidedBy },
        }),
      );
      if (updated) s.parts = updated;
      return s;
    }
    case 'step-finished': {
      const step = { ...ev.usage, costUsd: ev.costUsd, seq };
      const prev = state.steps[ev.stepIdx];
      s.steps = { ...state.steps, [ev.stepIdx]: step };
      const t = { ...state.totals };
      // Replace (not add) if the same step is reported twice.
      for (const k of Object.keys(ZERO) as (keyof StepUsage)[]) t[k] += step[k] - (prev?.[k] ?? 0);
      s.totals = t;
      return s;
    }
    case 'subagent-spawned':
      s.parts = [...state.parts, { kind: 'subagent', seq, childRunId: ev.childRunId, role: ev.role }];
      return s;
    case 'message-delivered':
      // Shown in the run as info rows: who sent what, handed to the agent mid-run.
      s.parts = [
        ...state.parts,
        ...ev.messages.map((m, i) => ({
          kind: 'log' as const,
          seq: seq + i / 100,
          level: 'info' as const,
          message: `Message delivered from ${m.from}: ${m.text}`,
        })),
      ];
      return s;
    case 'plan-submitted':
      s.parts = [
        ...state.parts,
        { kind: 'log', seq, level: 'info', message: `Plan v${ev.version} submitted: ${ev.plan.title}` },
      ];
      return s;
    case 'plan-reviewed':
      s.parts = [
        ...state.parts,
        {
          kind: 'log',
          seq,
          level: 'info',
          message: `Manager review: ${ev.review.outcome}. ${ev.review.reasoning}`,
        },
      ];
      return s;
    case 'plan-decided':
      s.parts = [
        ...state.parts,
        {
          kind: 'log',
          seq,
          level: 'info',
          message: `Plan decision: ${ev.decision}${ev.reason ? `. ${ev.reason}` : ''}`,
        },
      ];
      return s;
    case 'plan-execution':
      s.parts = [
        ...state.parts,
        { kind: 'log', seq, level: 'info', message: `Executing approved plan v${ev.version}` },
      ];
      return s;
    case 'log':
      s.parts = [...state.parts, { kind: 'log', seq, level: ev.level, message: ev.message }];
      return s;
    case 'egress-blocked':
      s.parts = [...state.parts, { kind: 'egress', seq, agentId: ev.agentId, hosts: ev.hosts }];
      return s;
    case 'model-fallback':
      s.parts = [...state.parts, fallbackPart(seq, ev)];
      s.activeModelRef = ev.toModelRef;
      if (ev.toReasoningEffort) s.activeReasoningEffort = ev.toReasoningEffort;
      else delete s.activeReasoningEffort;
      return s;
    case 'flow-step': {
      // One row per flow step, updated in place (started → waiting → ok / failed).
      const i = state.parts.findIndex((p) => p.kind === 'flow' && p.stepId === ev.stepId);
      const part: RunPart = {
        kind: 'flow',
        seq: i === -1 ? seq : (state.parts[i] as { seq: number }).seq,
        stepId: ev.stepId,
        stepKind: ev.kind,
        title: ev.title,
        status: ev.status,
        ...(ev.costUsd !== undefined ? { costUsd: ev.costUsd } : {}),
        ...(ev.error ? { error: ev.error } : {}),
      };
      s.parts = i === -1 ? [...state.parts, part] : state.parts.map((p, j) => (j === i ? part : p));
      return s;
    }
    case 'finish':
      s.finishReason = ev.finishReason;
      s.totals = { ...state.totals, costUsd: Math.max(state.totals.costUsd, ev.totalCostUsd) };
      return s;
  }
}

const fallbackPart = (seq: number, ev: Extract<RunEvent, { type: 'model-fallback' }>): FallbackPart => ({
  kind: 'fallback',
  seq,
  from: ev.from,
  to: ev.to,
  ...(ev.fromModelRef ? { fromModelRef: ev.fromModelRef } : {}),
  toModelRef: ev.toModelRef,
  ...(ev.toReasoningEffort ? { toReasoningEffort: ev.toReasoningEffort } : {}),
  reason: ev.reason,
});

/**
 * The fallback notices of a finished run, from its stored `run_events` rows (`runEvents?type=model-fallback`).
 * The chat shows finished runs from their saved message, which has no events, so this is how a reload keeps them.
 */
export function fallbackPartsFromEvents(
  rows: { seq: number; type: string; payload: unknown }[],
): FallbackPart[] {
  const out: FallbackPart[] = [];
  for (const r of rows) {
    const parsed = RunEvent.safeParse({ ...(r.payload as object), type: r.type });
    if (parsed.success && parsed.data.type === 'model-fallback') out.push(fallbackPart(r.seq, parsed.data));
  }
  return out;
}

/** Parse + reduce a raw SSE message. Invalid payloads are ignored (and warned in dev). */
export function reduceSSE(state: RunViewState, id: string | null, data: string): RunViewState {
  let json: unknown;
  try {
    json = JSON.parse(data);
  } catch {
    return state;
  }
  const parsed = RunEvent.safeParse(json);
  if (!parsed.success) {
    if (import.meta.env?.DEV) console.warn('[sse] invalid RunEvent', parsed.error.issues);
    return state;
  }
  const seq = id != null ? Number(id) : state.lastSeq + 1;
  return reduceRunEvent(state, Number.isFinite(seq) ? seq : state.lastSeq + 1, parsed.data);
}

/** In a finished run, a tool call that never got a result was interrupted — it is not still running. */
export function settleToolParts(parts: RunPart[], status: string | null | undefined): RunPart[] {
  if (!isTerminal(status as RunStatus | null | undefined)) return parts;
  if (!parts.some((p) => p.kind === 'tool' && (p.state === 'running' || p.state === 'awaiting_approval')))
    return parts;
  return parts.map((p) =>
    p.kind === 'tool' && (p.state === 'running' || p.state === 'awaiting_approval')
      ? { ...p, state: 'interrupted' as const }
      : p,
  );
}

export function isTerminal(status: RunStatus | null | undefined): boolean {
  return status != null && TERMINAL_RUN_STATUSES.has(status);
}

export function pendingApprovals(state: RunViewState): ToolPart[] {
  return state.parts.filter((p): p is ToolPart => p.kind === 'tool' && p.approval?.status === 'pending');
}

/** Convert persisted AI SDK UIMessage parts (MessageDto.parts) into RunParts for uniform rendering. */
export function uiPartsToRunParts(parts: Record<string, unknown>[]): RunPart[] {
  const out: RunPart[] = [];
  let stepIdx = 0;
  parts.forEach((p, i) => {
    const type = String(p.type ?? '');
    if (type === 'step-start') stepIdx++;
    else if (type === 'text') out.push({ kind: 'text', seq: i, stepIdx, text: String(p.text ?? '') });
    else if (type === 'reasoning')
      out.push({ kind: 'reasoning', seq: i, stepIdx, text: String(p.text ?? '') });
    else if (type.startsWith('tool-') || type === 'dynamic-tool') {
      const toolName = type === 'dynamic-tool' ? String(p.toolName ?? 'tool') : type.slice(5);
      const st = String(p.state ?? '');
      const approval = p.approval as { id?: string; approved?: boolean } | undefined;
      out.push({
        kind: 'tool',
        seq: i,
        stepIdx,
        toolCallId: String(p.toolCallId ?? i),
        toolName,
        input: p.input,
        effect: String(p.effect ?? ''),
        output: p.output ?? p.errorText,
        state:
          st === 'output-error'
            ? 'error'
            : st === 'output-denied' || approval?.approved === false
              ? 'denied'
              : st === 'approval-requested'
                ? 'awaiting_approval'
                : st === 'output-available'
                  ? 'done'
                  : 'running',
      });
    }
  });
  return out;
}
